<template>
    <VJoyBottomSheet ref="bottomSheetRef" :open="props.open">
        <template #bottom-sheet-content>
            <div ref="containerRef" class="filter-modal" :class="{isMobileScreen}" @click.stop>
                <div class="filter-modal_header / flex">
                    <VJoyIcon class="filter-modal_header_icon / flex" name="chevron-left" size="xsmall" @click="handleClose" />
                    <span class="filter-modal_header_heading / flex">{{ t('projectclient.projectlist.topbar.button.filter') }}</span>
                    <div class="filter-modal_header_reset / flex">
                        <GhostButton size="m" :disabled="!isAnyFilterSelected" @click="projectListFiltersStore.handleResetAllCategories">
                            {{ t('projectclient.projectlist.topbar.button.reset') }}
                        </GhostButton>
                    </div>
                </div>
                <div class="filter-modal_content / flex">
                    <FiltersGroup />
                </div>
            </div>
        </template>
        <template #bottom-sheet-cta>
            <div class="filter-modal_footer">
                <VJoyButton class="filter-modal_footer_show_results" :loading="loadingTemporaryResults" @click="handleShowResults">
                    {{ t('projectclient.projectlist.topbar.button.showResults', {0: temporaryNumberOfResults}) }}
                </VJoyButton>
                <GhostButton size="l" class="filter-modal_footer_cancel" @click="handleClose">
                    {{ t('projectclient.projectlist.topbar.button.cancel') }}
                </GhostButton>
            </div>
        </template>
    </VJoyBottomSheet>
</template>

<script setup lang="ts">
    import {useTemplateRef} from 'vue';
    import {useScreenSizeStore} from '@common/stores';
    import {GhostButton} from '@common/ui';
    import {useProjectsListFiltersStore} from '@features';
    import {storeToRefs} from 'pinia';
    import {useTranslation} from '#imports';
    import {useEventBus} from '@malt/utils';
    import {VJoyBottomSheet, VJoyButton, VJoyIcon} from '@maltjoy/core-vue';
    import FiltersGroup from '../wrappers/FiltersGroup.vue';
    import {eventBus} from '@/common/events';

    const props = defineProps<{open: boolean}>();

    const {t} = useTranslation();

    const screenSizeStore = useScreenSizeStore();
    const {isMobileScreen} = storeToRefs(screenSizeStore);

    const containerRef = useTemplateRef('containerRef');
    const bottomSheetRef = useTemplateRef('bottomSheetRef');

    const projectListFiltersStore = useProjectsListFiltersStore();
    const {isAnyFilterSelected, temporaryNumberOfResults, loadingTemporaryResults, ownerFilterProperties, freelancerFilterProperties} =
        storeToRefs(projectListFiltersStore);

    function handleClose() {
        bottomSheetRef.value?.hide();
        projectListFiltersStore.initTemporarySearchParams();
        ownerFilterProperties.value.resultsSize = 6;
        freelancerFilterProperties.value.resultsSize = 6;
    }

    function handleShowResults() {
        projectListFiltersStore.saveTemporaryParamsAndSearch();
        bottomSheetRef.value?.hide();
        ownerFilterProperties.value.resultsSize = 6;
        freelancerFilterProperties.value.resultsSize = 6;
    }

    useEventBus(eventBus, 'openProjectListFilterDrawer', () => {
        bottomSheetRef.value?.show();
    });
</script>

<style lang="scss" scoped>
    .filter-overlay {
        background-color: rgb(0 0 0 / 40%);
        position: absolute;
        height: 100vh;
        width: 100vw;
        inset: 0;
        z-index: 1000;
    }

    .filter-modal {
        background: var(--joy-color-background-default);
        padding: var(--joy-core-spacing-4);

        &_header {
            padding: 0 0 var(--joy-core-spacing-4) 0;
            align-items: center;
            position: sticky;
            top: 0;
            background-color: var(--joy-color-background-default);
            z-index: 10;

            & > * {
                flex: 1;
            }

            &_icon {
                cursor: pointer;
                justify-content: flex-start;
            }

            &_heading {
                font-size: var(--joy-font-size-primary-600);
                font-weight: var(--joy-font-weight-bold);
                justify-content: center;
            }

            &_reset {
                justify-content: flex-end;
            }

            &.isStucked {
                box-shadow: 0 -2px 10px rgb(0 0 0 / 15%);
            }
        }

        &_content {
            flex-flow: column nowrap;
            justify-content: flex-start;
            margin-bottom: var(--joy-core-spacing-8);
        }

        :deep(.joy-checkbox) {
            padding: var(--joy-core-spacing-2) 0;
            margin: 0 !important;
        }

        &.isMobileScreen {
            padding: 0;
        }
    }

    .filter-modal_footer {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
        width: 100%;
        padding: var(--joy-core-spacing-4) var(--joy-core-spacing-8);
        border-radius: var(--joy-core-radius-3) var(--joy-core-radius-3) 0 0;
        transition: box-shadow 0.2s ease;

        &_show_results {
            width: 100%;
        }
    }
</style>
